import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

import type { DataMeta, DataSource } from '@/api/types'

interface FreshnessState {
  page: DataMeta | null
  setPage: (meta: DataMeta | null) => void
}

const FreshnessContext = createContext<FreshnessState | null>(null)

/**
 * Where the top bar's freshness badge gets its answer. A page declares the
 * provenance of what it is showing (live daily metrics, demo KPIs) and the
 * badge follows it; with no declaration the badge falls back to the client's.
 */
export function FreshnessProvider({ children }: { children: ReactNode }) {
  const [page, setPage] = useState<DataMeta | null>(null)
  const value = useMemo(() => ({ page, setPage }), [page])
  return <FreshnessContext.Provider value={value}>{children}</FreshnessContext.Provider>
}

/** Declares the current page's data provenance for as long as it is mounted. */
export function usePageFreshness(meta: { source: DataSource; lastUpdatedAt: string | null } | null | undefined) {
  const setPage = useContext(FreshnessContext)?.setPage
  const source = meta?.source
  const lastUpdatedAt = meta?.lastUpdatedAt ?? null

  useEffect(() => {
    if (!setPage || !source) return
    setPage({ source, lastUpdatedAt })
    return () => setPage(null)
  }, [setPage, source, lastUpdatedAt])
}

export function usePageFreshnessValue(): DataMeta | null {
  return useContext(FreshnessContext)?.page ?? null
}
